import { ReportingControllerBaseConfig } from '../../../base';
import { ReportSectionItemController } from './sectionItemInterface';
import { ReportSectionItemViewProps } from './sectionItemProps';
import {
    calculateAverageScore,
    getColorSchemeForScore,
    getScoreLevelText,
    getScoreSummaryText,
} from './sectionItemHelper';
import { ReportSourceObject } from '@varos/research-react';
import { SourceItemProps } from '../../source';

export function createReportSectionItemController(
    config: ReportingControllerBaseConfig
): ReportSectionItemController {
    return {
        useProps(item, props): ReportSectionItemViewProps {
            const { section } = item;
            const { chapterNumber, index: sectionIndex } = props;

            const truncatedCount = item.sources.length - 1;
            const [first, ...restSources] = item.sources;
            const sourceFragments = first ? [first.displayName] : [];
            // const sourceFragments = first?.kind === 'external' ? [first.title] : [];
            if (truncatedCount > 0) {
                sourceFragments.push(`${truncatedCount} more`);
            }

            const sourceWithAction = restSources.length === 0 ? first : null;

            const sourceLabel = sourceWithAction
                ? `Read ${sourceWithAction.type.replaceAll('-', ' ')}`
                : null;

            // const sourceWithAction =
            //     first?.kind === 'page' && restSources.length === 0 ? first : null;

            // const sourceLabel =
            //     sourceWithAction?.kind === 'page'
            //         ? `Read ${sourceWithAction.kind.replaceAll('-', ' ')}`
            //         : null;

            const hasSource = item.sources.length > 0;

            const handleOpenSource = (source: ReportSourceObject) => {
                const url = source.url;
                if (url) {
                    window.open(url, '_blank');
                    return;
                }
                return props.onOpen(source);
            };

            return {
                id: section.id,
                title: `${props.chapterNumber}.${sectionIndex + 1}. ${section.title}`,
                caption: section.subtitle,
                // processedContent: processedContent,
                // processedContent: [],
                keywords: [],
                blocks: section.blocks.filter(
                    (block) => block.data.kind === 'text' || block.data.kind === 'heading'
                ),
                // keywords: section.keywords.map((keyword) => ({
                //     id: keyword.id,
                //     label: keyword.text,
                // })),
                action: sourceWithAction
                    ? {
                          kind: 'overlay',
                          label: sourceLabel ?? 'Read more',
                          button: {
                              onClick: props.onOpen.bind(null, sourceWithAction),
                          },
                      }
                    : null,
                // action: null,
                isLast: props.isLast,
                score: (() => {
                    // const allScores = section.sources.flatMap((source) => source.scores);
                    const allScores = item.sources
                        .flatMap((source) => source.scores)
                        .map((score) => ({
                            kind: score.id,
                            value: score.value,
                            displayName: score.label,
                            text: getScoreLevelText(score.value),
                        }));
                    if (allScores.length === 0) {
                        return null;
                    }

                    const averageScore = calculateAverageScore(allScores);
                    const summaryText = getScoreSummaryText(allScores);
                    const totalColorScheme =
                        averageScore !== null
                            ? getColorSchemeForScore(averageScore)
                            : 'orange';

                    const scoresByKind = allScores.reduce(
                        (acc, score) => {
                            const kind = score.kind;
                            if (!acc[kind]) {
                                acc[kind] = [];
                            }
                            acc[kind].push(score);
                            return acc;
                        },
                        {} as Record<string, typeof allScores>
                    );

                    const detailItems = Object.entries(scoresByKind)
                        .map(([kind, scoresArray]) => {
                            if (!scoresArray || scoresArray.length === 0) {
                                return null;
                            }
                            const averageValue =
                                scoresArray.reduce((sum, score) => sum + score.value, 0) /
                                scoresArray.length;

                            const representativeScore = scoresArray[0];
                            const colorScheme = getColorSchemeForScore(averageValue);

                            return {
                                id: `${section.id}-avg-score-${kind}`,
                                kind: kind,
                                label: representativeScore.displayName,
                                value: {
                                    numeric: averageValue,
                                    label: representativeScore.text,
                                },
                                progress: {
                                    colorScheme: colorScheme,
                                    value: averageValue * 10,
                                },
                            };
                        })
                        .filter((item) => item !== null)
                        // .sort((a, b) => {
                        //     const indexA = SCORE_KIND_ORDER.indexOf(a!.kind as ScoreKind);
                        //     const indexB = SCORE_KIND_ORDER.indexOf(b!.kind as ScoreKind);
                        //     return indexA - indexB;
                        // })
                        .map(({ kind, ...item }) => item as Exclude<typeof item, 'kind'>);

                    return {
                        total: {
                            colorScheme: totalColorScheme,
                            numeric: averageScore ?? 0,
                            text: summaryText,
                        },
                        detail: detailItems,
                    };
                })(),
                // score: null,
                // source: null,
                source: hasSource
                    ? {
                          label: sourceFragments.join(' and '),
                          image: null,
                          items: item.sources.map((source): SourceItemProps => {
                              return {
                                  onOpen: handleOpenSource.bind(null, source),
                                  score: {
                                      total: {
                                          numeric:
                                              source.scores.reduce(
                                                  (acc, score) => acc + score.value,
                                                  0
                                              ) / source.scores.length,
                                          colorScheme: getColorSchemeForScore(
                                              source.scores.reduce(
                                                  (acc, score) => acc + score.value,
                                                  0
                                              ) / source.scores.length
                                          ),
                                      },
                                  },
                                  references: {
                                      count: 0,
                                      label: 'References',
                                  },
                                  original: source.original,
                                  id: source.id,
                                  label: source.displayName,
                                  description: source.description ?? 'No description',
                                  image: null,
                                  //   image:
                                  //       source.kind === 'system'
                                  //           ? { src: Graphics.Brand.Icon }
                                  //           : null,
                                  //   anchor: externalUrl
                                  //       ? {
                                  //             id: source.id + '-anchor',
                                  //             href: externalUrl,
                                  //             children: 'View Source',
                                  //         }
                                  //       : null,
                              };
                          }),
                      }
                    : null,
            };
        },
    };
}
